import type { ComponentProps } from "react";

import { cn } from "../../lib/cn";
import { ChevronIcon } from "../icons";

export type SectionRowStatus = "ok" | "attention" | "idle";

const dotClassNames: Record<SectionRowStatus, string> = {
	ok: "bg-ok",
	attention: "bg-warn",
	idle: "bg-ink-4",
};

export interface SectionRowProps extends ComponentProps<"a"> {
	readonly name: string;
	readonly status: SectionRowStatus;
	readonly statusText: string;
}

/**
 * The dense status line: dot · name · status · chevron. It is an anchor: route with SectionRowLink. Without an `href` it
 * is the placeholder of a link that is not there yet — a page still to come — so it neither tints under the pointer nor
 * shows the chevron, whose room it keeps so that its status lines up with the other rows'.
 */
export function SectionRow({ name, status, statusText, className, ...props }: SectionRowProps) {
	const isLink = props.href !== undefined;
	return (
		<a
			className={cn(
				"flex items-center gap-2.5 border-b border-line px-4 py-3 transition-colors duration-100 last:border-0",
				isLink && "cursor-pointer hover:bg-hover-tint",
				className,
			)}
			{...props}
		>
			<span aria-hidden="true" className={cn("h-2 w-2 shrink-0 rounded-full", dotClassNames[status])} />
			<span className="flex-1 text-body font-medium text-ink">{name}</span>
			<span className="text-caption text-ink-3">{statusText}</span>
			{isLink ? (
				<ChevronIcon
					className="h-[11px] w-[11px] shrink-0 fill-none stroke-ink-4 stroke-[1.8]"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			) : (
				<span aria-hidden="true" className="w-[11px] shrink-0" />
			)}
		</a>
	);
}
